import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Mail, Lock, CheckCircle, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  // Mode: "request" (demande de reset) ou "reset" (nouveau mot de passe)
  const [mode, setMode] = useState(token ? "reset" : "request");
  
  // Request mode state
  const [email, setEmail] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  
  // Reset mode state
  const [tokenValid, setTokenValid] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Verify token on mount if present
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API}/api/customer/auth/verify-reset-token?token=${token}`);
      const data = await response.json();
      setTokenValid(data.valid);
      if (!data.valid) {
        setError(data.message || "Lien invalide ou expiré");
      }
    } catch (err) {
      setTokenValid(false);
      setError("Erreur de vérification du lien");
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API}/api/customer/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setRequestSent(true);
      } else {
        const data = await response.json();
        setError(data.detail || "Une erreur est survenue");
      }
    } catch (err) {
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (newPassword.length < 6) {
      setError("Le mot de passe doit faire au moins 6 caractères");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/api/customer/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: newPassword })
      });

      if (response.ok) {
        setResetSuccess(true);
      } else {
        const data = await response.json();
        setError(data.detail || "Une erreur est survenue");
      }
    } catch (err) {
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  // Success after password reset
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Mot de passe modifié !</h1>
            <p className="text-slate-600 mb-6">
              Tu peux maintenant te connecter avec ton nouveau mot de passe.
            </p>
            <Button
              onClick={() => navigate("/connexion")}
              className="w-full bg-pink-500 hover:bg-pink-600"
            >
              Se connecter
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Email sent confirmation
  if (requestSent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-pink-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Email envoyé !</h1>
            <p className="text-slate-600 mb-6">
              Si un compte existe avec l'adresse <strong>{email}</strong>, tu recevras un email avec un lien pour réinitialiser ton mot de passe.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Pense à vérifier tes spams si tu ne vois pas l'email.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate("/connexion")}
              className="w-full"
            >
              Retour à la connexion
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token
  if (mode === "reset" && tokenValid === false) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Lien invalide</h1>
            <p className="text-slate-600 mb-6">
              {error || "Ce lien de réinitialisation est invalide ou a expiré."}
            </p>
            <Button
              onClick={() => {
                setMode("request");
                setError("");
                setTokenValid(null);
              }}
              className="w-full bg-pink-500 hover:bg-pink-600 mb-3"
            >
              Demander un nouveau lien
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/connexion")}
              className="w-full"
            >
              Retour à la connexion
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading token verification
  if (mode === "reset" && tokenValid === null) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-pink-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/connexion")}
            className="flex items-center text-slate-600 hover:text-pink-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour
          </button>
          <img
            src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png"
            alt="Pressing Sakura"
            className="h-10 w-auto"
          />
          <div className="w-20"></div>
        </div>
      </nav>

      <div className="max-w-md mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {mode === "request" ? (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-pink-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Mot de passe oublié ?</h1>
                <p className="text-slate-600">
                  Entre ton email et on t'enverra un lien pour réinitialiser ton mot de passe.
                </p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Adresse email
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ton@email.com"
                    required
                    className="w-full"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-pink-500 hover:bg-pink-600"
                >
                  {loading ? "Envoi en cours..." : "Envoyer le lien"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-pink-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Nouveau mot de passe</h1>
                <p className="text-slate-600">
                  Choisis un nouveau mot de passe pour ton compte.
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nouveau mot de passe
                  </label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 caractères"
                    required
                    minLength={6}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Confirmer le mot de passe
                  </label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Retape ton mot de passe"
                    required
                    className="w-full"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-pink-500 hover:bg-pink-600"
                >
                  {loading ? "Modification..." : "Modifier le mot de passe"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
